"use client"

import React from "react"
import { useEffect, useState } from "react"
import axios from "axios"
import { QRCodeCanvas } from "qrcode.react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Link,
  Skeleton,
  Paper,
  Chip,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material"
import { Store, CalendarMonth, Update, Link as LinkIcon, QrCode2, Tag, CurrencyExchange } from "@mui/icons-material"

const Overview = ({ id }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const response = await axios.get(`/api/partner/store/${id}`)
        setData(response.data.data)
      } catch (error) {
        console.error("Error fetching data:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id])

  const InfoItem = ({ icon, label, value, isLink = false }) => (
    <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
      <Box sx={{ mr: 1, color: "primary.main" }}>{icon}</Box>
      <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontWeight: "medium" }}>
        {label}:
      </Typography>
      {isLink ? (
        <Link href={value} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: "break-all" }}>
          {value}
        </Link>
      ) : (
        <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
          {value}
        </Typography>
      )}
    </Box>
  )

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={3}>
        {loading ? (
          <>
            <Grid item xs={12} md={8}>
              <Skeleton variant="rectangular" height={300} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rectangular" height={300} />
            </Grid>
          </>
        ) : (
          data && (
            <>
              <Grid item xs={12} md={8}>
                <Card
                  elevation={3}
                  sx={{
                    height: "100%",
                    background: "linear-gradient(145deg, #ffffff 0%, #f5f7fa 100%)",
                    borderRadius: 2,
                    position: "relative",
                    overflow: "visible",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Store sx={{ mr: 1, color: "primary.main" }} />
                      <Typography variant="h5" component="h2" fontWeight="bold">
                        {data.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={data.id}
                        sx={{ ml: 2, bgcolor: "primary.light", color: "primary.contrastText" }}
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Stack spacing={1.5}>
                      <InfoItem
                        icon={<LinkIcon fontSize="small" />}
                        label="Domain"
                        value={data.sellsabeel_domain}
                        isLink={true}
                      />
                      <InfoItem icon={<Tag fontSize="small" />} label="Code" value={data.code} />
                      <InfoItem
                        icon={<CurrencyExchange fontSize="small" />}
                        label="Point Conversion Rate"
                        value={data.pointConversionRate.toString()}
                      />
                      <InfoItem
                        icon={<CalendarMonth fontSize="small" />}
                        label="Created At"
                        value={new Date(data.createdAt).toLocaleString()}
                      />
                      <InfoItem
                        icon={<Update fontSize="small" />}
                        label="Updated At"
                        value={new Date(data.updatedAt).toLocaleString()}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  elevation={3}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                    borderRadius: 2,
                    background: "linear-gradient(145deg, #f0f4f8 0%, #e6f0ff 100%)",
                    position: isMobile ? "relative" : "relative",
                    zIndex: isMobile ? 1 : "auto",
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                    <QrCode2 sx={{ mr: 1, color: "primary.main" }} />
                    Store QR Code
                  </Typography>

                  <Paper
                    elevation={4}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "white",
                      transform: "rotate(0deg)",
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "rotate(2deg) scale(1.02)",
                      },
                    }}
                  >
                    <QRCodeCanvas value={"https://cardzhub.com/qr/"+data.code} size={150} level="H" bgColor="#FFFFFF" fgColor="#000000" />
                  </Paper>

                  <Typography variant="caption" sx={{ mt: 2, color: "text.secondary", textAlign: "center" }}>
                    Scan to access store code: {data.code}
                  </Typography>
                </Card>
              </Grid>
            </>
          )
        )}
      </Grid>
    </Box>
  )
}

export default Overview

